import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import AddButton from "../buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import LoadingOverlay from "../helper-components/overlay-loading";
import { truncateString } from "../helper-components/helper-function";
import {
  purchaseTableAction,
  goodsTableAction,
  purchaseInvoiceTableAction,
  purchaseReturnTableAction,
  purchaseCreditTableAction,
} from "../../redux/actions/purchase-action";
import {
  salesTableAction,
  salesDeliveryTableAction,
  salesInvoiceTableAction,
  salesReturnTableAction,
  salesCreditTableAction,
} from "../../redux/actions/sales-action";

// Maps each document `name` (the prefix used across the create/edit screens) to
// the list action that powers its table page and the route used to view a row.
// This lets the same Past Bills panel be reused on every purchase/sales screen.
const DOC_CONFIG = {
  pot: { action: purchaseTableAction, viewRoute: "/view-purchase", title: "Past Purchase Orders" },
  gort: { action: goodsTableAction, viewRoute: "/view-goods", title: "Past Goods Receipts" },
  poit: { action: purchaseInvoiceTableAction, viewRoute: "/view-purchase-invoice", title: "Past Purchase Invoices" },
  prt: { action: purchaseReturnTableAction, viewRoute: "/view-purchase-return", title: "Past Purchase Returns" },
  pct: { action: purchaseCreditTableAction, viewRoute: "/view-purchase-credit", title: "Past Purchase Credit Notes" },
  sott: { action: salesTableAction, viewRoute: "/view-sales", title: "Past Sales Orders" },
  sodtt: { action: salesDeliveryTableAction, viewRoute: "/view-delivery", title: "Past Delivery Notes" },
  soit: { action: salesInvoiceTableAction, viewRoute: "/view-sales-invoice", title: "Past Sales Invoices" },
  srt: { action: salesReturnTableAction, viewRoute: "/view-sales-return", title: "Past Sales Returns" },
  sct: { action: salesCreditTableAction, viewRoute: "/view-sales-credit", title: "Past Sales Credit Notes" },
};

const EMPTY_FILTER = {
  sort_by: "",
  sort_order: "",
  selectColumn: "",
  from: "",
  to: "",
  inputValue: "",
  orderStatus: "",
};

export default function PastOrdersPanel({ open, setOpen, name, cmrId, cmrName }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const config = DOC_CONFIG[name];

  useEffect(() => {
    if (!open) return;
    if (!config?.action || !cmrId) {
      setOrders([]);
      return;
    }
    setLoading(true);
    // Server-filter to the selected vendor/customer so their records aren't
    // missed when they fall outside the first page of the (un-scoped) global
    // list. `cmr_name` is the customer column the list endpoints support for
    // search; we still narrow by exact `cmr_id` on the client to disambiguate
    // customers that share a name.
    const filterData = cmrName
      ? { ...EMPTY_FILTER, selectColumn: "cmr_name", inputValue: cmrName }
      : EMPTY_FILTER;
    dispatch(
      config.action(
        { pageNumber: 1, pageSize: 50, filterData },
        (data) => {
          const d = data?.data;
          const list = Array.isArray(d)
            ? d
            : d?.orderList ?? d?.salesList ?? d?.list ?? d?.allItemsData ?? [];
          const filtered = list.filter(
            (o) => String(o?.cmr_id) === String(cmrId)
          );
          setOrders(filtered);
          setLoading(false);
        },
        () => {
          setOrders([]);
          setLoading(false);
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cmrId, cmrName, name]);

  const getNumber = (o) =>
    o?.[`${name}_invoice_number`] ??
    o?.invoice_number ??
    o?.[`${name}_number`] ??
    "--";
  const getAmount = (o) => {
    const amt = o?.[`${name}_total_amount`] ?? o?.total_amount;
    return amt == null || amt === "" ? "--" : `₹ ${amt}`;
  };
  const getDate = (o) =>
    o?.created_date ? moment(o.created_date).format("DD MMM YYYY") : "--";
  const getId = (o) => o?.[`${name}_id`] ?? o?.id;
  const getStatus = (o) => o?.[`${name}_order_status`] ?? o?.order_status ?? "--";

  const openRow = (o) => {
    const id = getId(o);
    if (id && config?.viewRoute) {
      setOpen(false);
      navigate(`${config.viewRoute}/${id}`);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full w-[400px] sm:w-[720px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                {/* Header */}
                <div
                  className="h-[61px] bg-[var(--slide-panel-head-bg)] px-3 flex items-center justify-between border-b"
                  style={{ borderColor: "var(--slide-panel-border)" }}
                >
                  <div
                    style={{
                      maxHeight: "32px",
                      color: "var(--slide-panel-h-fg)",
                      fontSize: "var(--slide-panel-h-fs)",
                      fontWeight: "var(--slide-panel-h-fw)",
                    }}
                    className="min-w-[75%]"
                  >
                    {config?.title ?? "Past Bills"}
                    {cmrName ? ` - ${cmrName}` : ""}
                  </div>
                  <AddButton
                    onclkFn={() => setOpen(false)}
                    icon={
                      <CloseIcon
                        width="12px"
                        height="12px"
                        fill="var(--close-button-fg)"
                      />
                    }
                    text={
                      <span>
                        Close <span className="opacity-50">(X)</span>{" "}
                      </span>
                    }
                    className="flex h-[32px] items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                  />
                </div>

                {/* Body */}
                <div className="p-3" style={{ height: `calc(100% - 61px)` }}>
                  <div className="tab-container h-full border border-[var(--create-table-border)] rounded-[8px]">
                    <table className="w-full border-collapse mt-0">
                      <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                        <tr className="relative h-[50px]">
                          {PAST_ORDER_HEADING.map((heading, index) => (
                            <th
                              key={index}
                              style={{ minWidth: `${heading.minWidth}px` }}
                              className="px-3 whitespace-nowrap text-left text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                            >
                              {heading.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="h-full">
                        {orders?.length > 0 ? (
                          orders.map((rowData, rowIndex) => (
                            <tr
                              onClick={() => openRow(rowData)}
                              className="h-[50px] cursor-pointer hover:bg-[var(--create-table-row-hover-bg)] bg-[var(--create-table-row-bg)]"
                              key={rowIndex}
                            >
                              <td className="border-b border-[var(--create-table-border)] px-3 text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap">
                                {truncateString(getNumber(rowData), 40)}
                              </td>
                              <td className="border-b border-[var(--create-table-border)] px-3 text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap">
                                {getDate(rowData)}
                              </td>
                              <td className="border-b border-[var(--create-table-border)] px-3 text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap capitalize">
                                {truncateString(String(getStatus(rowData)), 20)}
                              </td>
                              <td className="border-b border-[var(--create-table-border)] px-3 text-right text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap">
                                {getAmount(rowData)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={PAST_ORDER_HEADING.length}
                              className="text-center py-6 text-[length:var(--tab-empty-comp-fs)] border-b border-[var(--create-table-border)] font-[var(--tab-empty-comp-fw)] text-[var(--tab-empty-comp-fg)]"
                            >
                              {cmrId
                                ? "No past records found"
                                : "Select a vendor/customer to view past records"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <LoadingOverlay isLoading={loading} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

const PAST_ORDER_HEADING = [
  { label: "Number", minWidth: 160 },
  { label: "Date", minWidth: 120 },
  { label: "Status", minWidth: 100 },
  { label: "Amount", minWidth: 110 },
];
